// Edit-sequence-guarded draft autosave (typed help/intake framework, commit 4).
//
// Extracted from the commit-2 support-ticket form, whose save loop had four guards
// that were each load-bearing and each easy to get wrong a second time. Rather than
// copy them into the request page, they live here once:
//
//   editSequence  Every edit bumps a counter. A save captures the counter it
//                 started at; when it resolves, the result is only allowed to
//                 declare "saved" if the counter has not moved. Without this, a
//                 slow save that lands after three more keystrokes marks the draft
//                 clean while three edits are still unsent.
//
//   saveChain     Saves are queued, never concurrent. Two in-flight PATCHes on the
//                 same aggregate would race on `expectedRevision` and one would
//                 always lose with a 409 the reporter did nothing to deserve.
//
//   dirty         Only a real edit arms the debounce, so simply OPENING a form
//                 never creates a draft the reporter did not ask for.
//
//   'pending'     The only state that arms a save. A FAILED save leaves the draft
//                 dirty; without this guard the effect would re-arm on the next
//                 render and retry every 1.2 s forever, so an offline reporter
//                 would hammer the API. A new edit sets 'pending' again, which is
//                 exactly when a retry is worth making.
//
// The hook owns the mechanics and nothing else. WHAT gets sent, and every message
// the reporter reads, stays with the caller: the two verticals describe their
// drafts differently and a shared string would be wrong for both.

import { useCallback, useEffect, useRef, useState } from 'react';

export const INTAKE_AUTOSAVE_DELAY_MS = 1200;

/**
 * @param {object}   options
 * @param {Function} options.buildPayload  () => payload for the current form state.
 * @param {Function} options.persist       (payload) => Promise<savedAggregate>.
 * @param {boolean}  options.enabled       False suspends the debounce (e.g. on a
 *                                         review step) without discarding state.
 * @param {Array}    options.watch         Values whose change should re-arm the
 *                                         debounce. Pass the edited form state.
 * @param {Function} [options.onConflict]   (error) => Promise<string|null>. Given a
 *                                         revision conflict, the caller refreshes
 *                                         and returns the message to show. Its
 *                                         typed work is never discarded here.
 * @param {Function} [options.describeError] (error) => string.
 * @param {string}   [options.savedMessage]
 * @param {string}   [options.savingMessage]
 */
export function useIntakeDraftAutosave({
  buildPayload,
  persist,
  enabled = true,
  watch = [],
  onConflict = null,
  describeError = null,
  savedMessage = 'Draft saved. You can close this page and come back to it.',
  savingMessage = 'Saving your draft…',
  delayMs = INTAKE_AUTOSAVE_DELAY_MS,
}) {
  const [saveState, setSaveState] = useState('idle');
  const [saveMessage, setSaveMessage] = useState('');

  const editSequenceRef = useRef(0);
  const dirtyRef = useRef(false);
  const saveChainRef = useRef(Promise.resolve());
  const timerRef = useRef(null);

  const markDirty = useCallback(() => {
    editSequenceRef.current += 1;
    dirtyRef.current = true;
    setSaveState('pending');
    setSaveMessage('');
  }, []);

  const save = useCallback(
    ({ announce = true } = {}) => {
      const startedAt = editSequenceRef.current;
      const payload = buildPayload();
      const run = async () => {
        setSaveState('saving');
        if (announce) setSaveMessage(savingMessage);
        try {
          const saved = await persist(payload);
          if (editSequenceRef.current === startedAt) {
            dirtyRef.current = false;
            setSaveState('saved');
            setSaveMessage(savedMessage);
          } else {
            // More edits arrived while this save was in flight. The draft is NOT
            // clean, and saying so would lose them.
            setSaveState('pending');
          }
          return saved;
        } catch (error) {
          if (editSequenceRef.current !== startedAt) {
            setSaveState('pending');
            setSaveMessage('');
            return null;
          }
          setSaveState('failed');
          if (
            error?.code === 'CONFLICT' &&
            error?.details?.reason === 'revision_mismatch' &&
            onConflict
          ) {
            const message = await onConflict(error);
            setSaveMessage(message || '');
            return null;
          }
          setSaveMessage(
            (describeError && describeError(error)) ||
              'PRIME could not save your draft. Your text is still here — try again.',
          );
          return null;
        }
      };
      const queued = saveChainRef.current.catch(() => null).then(run);
      saveChainRef.current = queued.catch(() => null);
      return queued;
    },
    [buildPayload, describeError, onConflict, persist, savedMessage, savingMessage],
  );

  useEffect(() => {
    if (!dirtyRef.current || saveState !== 'pending' || !enabled) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void save({ announce: false });
    }, delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // `watch` is the caller's form state; spreading it is what re-arms the debounce
    // on each edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayMs, enabled, save, saveState, ...watch]);

  return {
    saveState,
    saveMessage,
    setSaveMessage,
    markDirty,
    save,
    isDirty: () => dirtyRef.current,
  };
}
